import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import Button from '../components/common/Button'
import Card from '../components/common/Card'
import Spinner from '../components/common/Spinner'
import { MemberDisc } from '../components/social/TeamCard'
import UserLink from '../components/social/UserLink'
import { CATEGORY_OPTIONS, boardRequest, emptyLabel, entryValueLabel, isCalendarBoard, subtitleOf, teamValueLabel } from '../lib/leaderboard'
import { faDigits } from '../lib/numerals'
import { PERIODS } from '../lib/social'
import { teamName } from '../lib/teams'
import { classifyFailure } from '../services/api'
import { leaderboard as fetchLeaderboard } from '../services/social'

/**
 * The rows the server sent (top 20 + the caller's own row, B6), the caller's
 * in the brand colour. `category` and `calendar` (V6-w) only change what a
 * row's number says and the empty sentence; the legacy board passes neither.
 */
export function LeaderboardTable({ board, period, category = 'xp', calendar = false }) {
  const { t } = useTranslation('leaderboard')
  if (calendar && category === 'team' && board.team === null) {
    return <p className="py-2 text-sm text-ink-2" data-empty="no-team">{t('empty.noTeam')}</p>
  }
  if (board.items.length === 0) {
    return <p className="py-2 text-sm text-ink-2">{emptyLabel(period, category, calendar)}</p>
  }
  const meListed = board.items.some((entry) => entry.author.me)
  return (
    <>
      <ol className="divide-y divide-line">
        {board.items.map((entry) => (
          <li key={entry.author.id} data-me={entry.author.me ? 'true' : undefined} className={`flex items-center gap-3 py-2.5 text-sm ${entry.author.me ? 'font-bold text-brand-700' : 'text-ink'}`}>
            <span className="tnum w-6 text-ink-3">{faDigits(entry.rank)}</span>
            {/* The learner's photo where `social.photosEnabled` sends one, the shop-avatar disc otherwise. */}
            <MemberDisc avatar={entry.author.avatar} photoUrl={entry.author.photoUrl} size={36} />
            <span className="min-w-0 flex-1 truncate">
              <UserLink userId={entry.author.id}>{entry.author.displayName}</UserLink>
              {entry.author.me && <span className="ms-2 rounded-full bg-brand-50 px-2 py-0.5 text-[10.5px] font-semibold text-brand-700">{t('youTag')}</span>}
            </span>
            <span className="tnum text-brand-700">{entryValueLabel(category, entry)}</span>
          </li>
        ))}
      </ol>
      {board.me && !meListed && (
        <p className="tnum border-t border-line py-2 text-xs text-ink-2">
          {category === 'xp' || category === 'team' || !calendar
            ? t('myRank', { rank: faDigits(board.me.rank), xp: faDigits(board.me.xp) })
            : t('myRankValue', { rank: faDigits(board.me.rank), value: entryValueLabel(category, board.me) })}
        </p>
      )}
    </>
  )
}

/** The best teams (`category=teams`, V6-b-leaderboard): `teams` and the caller's `myTeam` wherever it ranks. */
export function TeamsTable({ board }) {
  const { t } = useTranslation('leaderboard')
  const teams = Array.isArray(board.teams) ? board.teams : []
  if (teams.length === 0) {
    return <p className="py-2 text-sm text-ink-2">{t('empty.teams')}</p>
  }
  const mineListed = teams.some((row) => row.mine)
  return (
    <>
      <ol className="divide-y divide-line">
        {teams.map((row) => (
          <li key={row.team.id} data-me={row.mine ? 'true' : undefined} className={`flex items-center gap-3 py-2.5 text-sm ${row.mine ? 'font-bold text-brand-700' : 'text-ink'}`}>
            <span className="tnum w-6 text-ink-3">{faDigits(row.rank)}</span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate">
                {teamName(row.team)}
                {row.mine && <span className="ms-2 rounded-full bg-brand-50 px-2 py-0.5 text-[10.5px] font-semibold text-brand-700">{t('myTeamTag')}</span>}
              </span>
              <span className="tnum text-[11px] font-normal text-ink-3">{t('membersCount', { count: faDigits(row.members) })}</span>
            </span>
            <span className="tnum text-brand-700">{teamValueLabel(row)}</span>
          </li>
        ))}
      </ol>
      {board.myTeam && !mineListed && (
        <p className="tnum border-t border-line py-2 text-xs text-ink-2">{t('myTeamRank', { rank: faDigits(board.myTeam.rank), value: teamValueLabel(board.myTeam) })}</p>
      )}
    </>
  )
}

/**
 * The leaderboard tab (V3-w): the table that used to sit at the foot of
 * the feed (P-feed, B6's `social_leaderboard`), on its own page now that
 * it is one of the four destinations. Week or month, the server's rows,
 * the learner's own rank under the table when it is not among them; a
 * door to the feed at the bottom, since the feed is no longer a tab.
 *
 * V6-w: once the server answers the legacy board as a calendar board
 * (`periodKey` present — the owner turned on
 * `leaderboard.calendarPeriodsEnabled`), the categories of
 * V6-b-leaderboard appear under the pills. Until then nothing changes:
 * same request, same rows, no category tabs (`lib/leaderboard.js`).
 */
export default function Leaderboard() {
  const { t } = useTranslation('leaderboard')
  const [period, setPeriod] = useState('weekly')
  const [category, setCategory] = useState('xp')
  const [calendar, setCalendar] = useState(false)
  const [board, setBoard] = useState(null)
  const [error, setError] = useState(null)
  const [reload, setReload] = useState(0)

  // The request as a key, so learning that calendar boards are on does not refetch the XP board just read.
  const requestKey = JSON.stringify(boardRequest(period, category, calendar))

  useEffect(() => {
    let cancelled = false
    setError(null)
    const request = JSON.parse(requestKey)
    fetchLeaderboard(request.period, request.options)
      .then((data) => {
        if (cancelled) return
        // Only the XP board — the legacy request — tells whether calendar boards are on.
        if (category === 'xp') setCalendar(isCalendarBoard(data))
        setBoard(data)
      })
      .catch((e) => {
        if (!cancelled) setError(e)
      })
    return () => {
      cancelled = true
    }
  }, [requestKey, category, reload])

  return (
    <div className="mx-auto max-w-md space-y-3.5">
      <header className="flex items-center gap-2">
        <div className="flex-1">
          <h1 className="text-xl font-bold text-ink">{t('title')}</h1>
          <p className="text-xs leading-[1.8] text-ink-2">{subtitleOf(calendar, board?.periodKey)}</p>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-1 rounded-xl bg-surface-2 p-1" role="group" aria-label={t('periodAriaLabel')}>
        {PERIODS().map((option) => (
          <button
            key={option.key}
            type="button"
            aria-pressed={period === option.key}
            onClick={() => {
              if (option.key === period) return
              setBoard(null)
              setPeriod(option.key)
            }}
            className={`h-9 rounded-lg text-[12.5px] font-semibold transition-colors ${period === option.key ? 'bg-surface text-brand-700 shadow-sm' : 'text-ink-2'}`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {calendar && (
        <div className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-0.5" role="group" aria-label={t('categoryAriaLabel')}>
          {CATEGORY_OPTIONS().map((option) => (
            <button
              key={option.key}
              type="button"
              aria-pressed={category === option.key}
              onClick={() => {
                if (option.key === category) return
                setBoard(null)
                setCategory(option.key)
              }}
              className={`h-8 shrink-0 rounded-full border px-3 text-[12px] font-semibold transition-colors ${category === option.key ? 'border-brand-600 bg-brand-50 text-brand-700' : 'border-line bg-surface text-ink-2'}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {error && !board ? (
        <div className="space-y-3">
          <div role="status" className={`rounded-xl p-3 text-sm ${classifyFailure(error) === 'offline' ? 'bg-warn-tint text-warn-ink' : 'bg-bad-tint text-bad-ink'}`}>
            {classifyFailure(error) === 'offline' ? t('errors.offline') : t('errors.server')}
          </div>
          <Button variant="secondary" className="w-full" onClick={() => setReload((n) => n + 1)}>
            {t('errors.retry')}
          </Button>
        </div>
      ) : !board ? (
        <Spinner label={t('loading')} />
      ) : (
        <Card as="div" pad="px-4 py-2">
          {calendar && category === 'teams' ? (
            <TeamsTable board={board} />
          ) : (
            <LeaderboardTable board={board} period={period} category={calendar ? category : 'xp'} calendar={calendar} />
          )}
        </Card>
      )}

      <Link to="/social" className="flex h-14 items-center justify-between card-soft rounded-tile px-4 text-sm font-medium text-ink transition hover:bg-surface-2">
        <span>{t('feedCta')}</span>
        <span className="text-xs text-ink-3">{t('feedHint')}</span>
      </Link>
    </div>
  )
}
